import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { DOC_STATUS, fmtDate } from "../lib/re";
import { Badge, Spinner, Empty, Pagination } from "../components/re/ui";
import { DocPreview } from "../components/re/PropertyDocs";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, FileX2, Search, Eye } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="card-almar p-5 text-left flex items-center gap-4">
    <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone}`}><Icon size={20} /></span>
    <div><p className="text-2xl font-semibold text-slate-100">{value}</p><p className="text-xs text-slate-400">{label}</p></div>
  </button>
);

export default function Documentacao() {
  const [alerts, setAlerts] = useState(null);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ status: "", category: "", q: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/re/documents-alerts").then((r) => setAlerts(r.data)).catch(() => setAlerts({ properties: [], missing_total: 0, expired: 0, expiring: 0 }));
    api.get("/re/settings").then((r) => setCats(r.data.doc_categories)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/re/documents", { params: { ...p, page } }).then((r) => setData(r.data))
      .catch(() => { toast.error("Erro ao carregar documentos"); setData({ items: [], total: 0, pages: 1, page: 1 }); });
  }, [f, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const setFilter = (k, v) => { setF({ ...f, [k]: v }); setPage(1); };

  return (
    <Layout title="Documentação">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat icon={FileX2} label="Documentos em falta (checklist)" value={alerts?.missing_total ?? "—"} tone="bg-red-500/15 text-red-300" testId="alert-missing" onClick={() => document.getElementById("missing-list")?.scrollIntoView({ behavior: "smooth" })} />
        <Stat icon={AlertTriangle} label="Documentos expirados" value={alerts?.expired ?? "—"} tone="bg-orange-500/15 text-orange-300" testId="alert-expired" onClick={() => setFilter("status", "expirado")} />
        <Stat icon={CalendarClock} label="A expirar em 30 dias" value={alerts?.expiring ?? "—"} tone="bg-amber-500/15 text-amber-300" testId="alert-expiring" onClick={() => setFilter("status", "a_expirar")} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input className="input-almar pl-9" placeholder="Pesquisar documentos" value={f.q} onChange={(e) => setFilter("q", e.target.value)} data-testid="docs-search-input" />
            </div>
            <select className="input-almar md:w-44" value={f.status} onChange={(e) => setFilter("status", e.target.value)} data-testid="docs-filter-status">
              <option value="">Todos os estados</option><option value="a_expirar">A expirar (30 dias)</option>
              {Object.entries(DOC_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select className="input-almar md:w-56" value={f.category} onChange={(e) => setFilter("category", e.target.value)} data-testid="docs-filter-category">
              <option value="">Todas as categorias</option>{cats.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {!data ? <Spinner /> : data.items.length === 0 ? <Empty text="Nenhum documento nesta vista." /> : (
            <div className="space-y-2" data-testid="agency-docs-list">
              {data.items.map((d) => (
                <div key={d.id} className="card-almar p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`agency-doc-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-100 truncate">{d.name}</p>
                    <p className="text-xs text-slate-500 truncate"><Link to={`/imoveis/${d.property_id}`} className="hover:text-[#E0C574]">{d.property_ref} · {d.property_title}</Link> · {d.category} · {fmtDate(d.created_at)}{d.expiry_date ? ` · Validade ${fmtDate(d.expiry_date)}` : ""}</p>
                  </div>
                  <Badge value={d.status} label={DOC_STATUS[d.status]} />
                  {d.has_file && <button onClick={() => setPreview(d)} data-testid={`agency-doc-preview-${d.id}`} className="p-2 text-slate-400 hover:text-[#C9A84C]"><Eye size={16} /></button>}
                </div>
              ))}
            </div>
          )}
          {data && data.items.length > 0 && <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />}
        </div>
        <div id="missing-list" className="card-almar p-5 h-fit" data-testid="missing-docs-panel">
          <h3 className="font-display text-lg text-slate-100 mb-3">Imóveis com documentação em falta</h3>
          {!alerts ? <Spinner /> : alerts.properties.length === 0 ? <p className="text-sm text-slate-500">Toda a documentação obrigatória está completa.</p> : alerts.properties.map((p) => (
            <Link key={p.id} to={`/imoveis/${p.id}`} className="block py-3 border-b border-[#1E3A56]/60 last:border-0" data-testid={`missing-prop-${p.id}`}>
              <p className="text-sm text-slate-100 truncate"><span className="font-mono text-xs text-slate-400 mr-2">{p.reference}</span>{p.title}</p>
              <p className="text-xs text-amber-300/90 mt-0.5">{p.missing.join(" · ")}{p.expired ? ` · ${p.expired} expirado(s)` : ""}</p>
            </Link>
          ))}
        </div>
      </div>
      {preview && <DocPreview d={preview} onClose={() => setPreview(null)} />}
    </Layout>
  );
}
